import { getEnvironmentConfigFromRing, getRingForEnvironment } from '@fluidx/utilities';
import { getCachedPrefetchEntryForDeploymentRing, cachePrefetchEntryForDeploymentRing } from './ManifestCacheHelpers';
import { getCachedOdspDriverManifest, fetchOdspDriverManifest } from './OdspDriverManifestAccessors';
import { currentVersionKey } from './OdspDriverManifestType';
/**
 * Pre-fetches the ODSP driver files into the browser cache, to gain a performance boost.
 * This function looks fetches the latest ODSP deployment manifest and JS files,
 * or no-ops if these files are already in the cache.
 * @returns urls to driver main bundle which can be passed by hosts calling V2 sourceless iframe (e.g., `loadComponentInSourcelessIframeV2`) into the `driverPrecacheResult` param
 * to yield performance optimization. Otherwise, this method can be simply invoked as a fire-and-forget, with no need to await or do anything with the result.
 */
export async function precacheDynamicOdspDriver(config = {}) {
    const deploymentRing = getRingForEnvironment(config.environment);
    const manifest = getCachedOdspDriverManifest(deploymentRing) || (await fetchOdspDriverManifest(deploymentRing));
    const currentVersionEntry = manifest[currentVersionKey];
    const driverPrecacheResult = {
        mainBundleUrls: [manifest.jsBaseUrl + '/' + currentVersionEntry.index]
    };
    // It's OK to do the preload on the top-level window context (it doesn't add script tags, only link tags with "preload"/"prefetch" type.)
    // eslint-disable-next-line no-restricted-globals -- see explanation above */
    const targetDocument = document;
    const prefetchCacheEntry = getCachedPrefetchEntryForDeploymentRing(deploymentRing);
    // compare index file names and do a prefetch only if there is a mismatch
    if (!prefetchCacheEntry || prefetchCacheEntry !== currentVersionEntry.index) {
        // Exception added when eslint rule was added, this should be revisited when modifying this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const precacheFileLinks = [currentVersionEntry.index, ...(currentVersionEntry.additionalFiles || [])].map((filename) => manifest.jsBaseUrl + '/' + filename);
        // Using a document fragment here to reduce the number of writes to the dom
        const fragment = targetDocument.createDocumentFragment();
        precacheFileLinks.forEach((urlToPrecache) => {
            const prefetchTag = targetDocument.createElement('link');
            prefetchTag.rel = 'prefetch';
            prefetchTag.href = urlToPrecache;
            prefetchTag.as = 'script';
            prefetchTag.crossOrigin = 'anonymous';
            fragment.appendChild(prefetchTag);
        });
        targetDocument.head.appendChild(fragment);
        cachePrefetchEntryForDeploymentRing(currentVersionEntry.index, deploymentRing);
    }
    return driverPrecacheResult;
}
/**
 * @deprecated [Please use "precacheDynamicOdspDriver" instead. In the future, this API will be removed in favor of the new one]
 *
 * Pre-fetches the ODSP driver files into the browser cache, to gain a performance boost.
 * This function looks fetches the latest ODSP deployment manifest and JS files,
 * or no-ops if these files are already in the cache.
 */
export async function prefetchDynamicOdspDriver(environment) {
    await precacheDynamicOdspDriver({ environment: getEnvironmentConfigFromRing(environment) });
}
//# sourceMappingURL=precache.js.map